export default {
    required: 'required',
    invite: {
        panelTitle: 'Invite New Agents',
        intro: 'Invite agents to your agency by manually entering their information below or by uploading a CSV file containing information about each agent to invite. Your agency currently has <strong>{{pending}}</strong> pending invitations (max: <strong>{{max}}</strong>).',
        maxExceeded: 'Your agency has reached the maximum number of pending invitations. Please wait for your travel agents to accept the invitations that have been sent or revoke some pending invitations.',
        columns: {
            title: 'Title',
            firstName: 'First Name',
            lastName: 'Last Name',
            email: 'Email Address',
            role: 'Role',
            agent: 'Agent',
            masterAgent: 'Master Agent'
        },
        addAgent: 'Add Another Agent',
        addAgentDescription: 'Click to add another agent to the list.',
        uploadCsv: 'Upload CSV File',
        uploadCsvDescription: 'Click to upload a CSV file of agents.',
        deleteRow: 'Delete Row',
        clearAll: 'Clear All',
        clearAllDescription: 'Click to remove all agents.',
        submit: 'Send Invitations',
        submitting: 'Processing...',
        csvModal: {
            modalTitle: 'Upload CSV File',
            label: 'Quickly invite multiple agents by uploading a <strong>Comma-Separated Values</strong> (CSV) file.',
            placeholder: 'Choose a file...',
            explanation: '<p>The CSV file should contain one line per agent, each of which includes the following columns. The order of the columns is not important, but the first name must come before the last name.</p>\n<ul><li>Title (optional, possible values: <code>Mr.</code>, <code>Ms.</code>, <code>Mrs.</code>, <code>Dr.</code>)</li><li>First Name</li><li>Last Name</li><li>Email Address</li><li>Role (optional, possible values: <code>agent</code>, <code>master</code>)</li></ul><p>Example of a well formed CSV file:</p><pre class="p-2 bg-secondary-light rounded">Mr.,John,Doe,john.doe@agenciaglobal.com,agent\nMs.,Jane,Doe,jane.doe@agenciaglobal.com,master</pre>',
            tooManyAgents: 'Your uploaded CSV contains more agents than you have available invitations. You will be able to send more after your travel agents accept their invitations.',
        },
        validation: {
            noAgents: 'You must add at least one agent.',
            invalid: 'Some invitations are invalid, please correct the errors above before proceeding.',
            duplicateEmail: 'You cannot send multiple invites to the same email address.'
        }
    },
    flight: {
        baseFare: 'Base Fare',
        taxes: 'Taxes',
        taxesFees: 'Taxes &amp; Fees',
        subtotal: 'Subtotal',
        quantity: 'Quantity',
        total: 'Total',
        grandTotal: 'Grand Total',
        passengerType: 'Passenger Type',
        passengerTypes: {
            adt: 'Adult',
            chd: 'Child',
            inl: 'Infant (lap)',
            ins: 'Infant (seat)'
        },
        from: 'From {{price}}',
    },
    quote: {
        deleteConfirm: 'Are you sure you want to delete this quote?',
        unsubscribeConfirm: 'Are you sure you want to unsubscribe this fare alert?',
        notFound: 'Your saved quote is no longer available.'
    },
    insurance: {
        options: {
            none: 'No Coverage'
        }
    },
    breakdown: {
        panelTitle: 'Price Breakdown',
        pricingCurrency: 'Prices are displayed in',
        ctCommissionNotice: 'Commission on this reservation is only calculated after the rental is completed, but typically amounts to between 5% to 15% of the rental price. It may take approximately 30 days for Agencia Global to reconcile with the supplier, after which your agency will receive the commission payout.<br><br>Any additional markup added to the booking is collected immediately and will be paid out on the next statement.',
        header: {
            commission: 'Commission',
            commissionTbd: 'TBD',
            agencyMarkup: 'Agency Markup',
            merchantFee: 'Service Fee',
            totalMarkup: 'Total Markup',
            maxMarkupTooltip: 'Maximum markup: {{markup}}',
            merchantFeeTooltip: 'All markup will be charged on the Agencia Global merchant account and incur a 3% service fee (plus applicable taxes).',
        },
        charges: {
            cardTitle: 'Charges on the customer\'s credit card',
            merchants: {
                car: 'prepaid on {{supplier}}\'s merchant account',
                car_poa: 'to be paid on arrival at {{supplier}}',
                agencia: 'on Agencia Global\'s merchant account'
            },
            currencyConversionNotice: '(Estimated in {{currency}} using current exchange rates, may be charged in local currency)',
            merchantFeeNotice: 'includes {{merchantFee}}% service fee of',
            total: 'Total Charge'
        },
        payout: {
            cardTitle: 'Agency Payout',
            markup: 'Markup',
            commission: 'Commission',
            total: 'Total Payout'
        },
        tax: 'tax',
    },
    checkout: {
        errors: {
            generic: 'We were unable to book this flight at this time, please try again later.',
            genericTitle: 'Booking Failed',
            expired: 'This session has expired, please begin a new search'
        }
    },
    cars: {
        search: {
            loading: {
                panelTitle: 'Searching Available Vehicles',
            },
            header: {
                panelTitle: 'Search Results for Your Itinerary',
                pickup: 'Pickup from {{location}} on {{date}}',
                return: 'Return to {{location}} on {{date}}',
                currency: 'Currency',
                driverAge: 'Driver Age',
                driverCountry: 'Driver Country',
                refineSearch: 'Refine Your Search',
                redoSearch: 'Redo Search'
            },
            filter: {
                panelTitle: 'Filter Your Search Results',
                apply: 'Apply Filters',
                reset: 'Reset Filters',
                label: {
                    price: 'Price',
                    suppliers: 'Suppliers',
                    categories: 'Categories',
                    passengers: 'Passengers',
                    baggage: 'Baggage',
                    doors: 'Doors',
                    transmissionTypes: 'Transmission Types',
                    driveTypes: 'Drive Types',
                    fuelTypes: 'Fuel Types',
                    airConditioning: 'Air Conditioning',
                    rateQualifiers: 'Rate Qualifiers',
                    fuelPolicies: 'Fuel Policies',
                    mileage: 'Mileage',
                    pickupLocation: 'Pickup Locations',
                    returnLocation: 'Return Locations'
                }
            },
            results: {
                panelTitle: 'Vehicles Available for Rental',
                of: 'of',
                orSimilar: 'or similar',
                passengerTooltip: 'Number of Passengers',
                baggageTooltip: 'Baggage Capacity',
                doorTooltip: 'Number of Doors',
                pickupLocation: 'Pickup Location',
                returnLocation: 'Return Location',
                mileageAllowance: 'Mileage Allowance',
                fuelPolicy: 'Fuel Policy',
                fuelType: 'Fuel Type',
                transmissionType: 'Transmission Type',
                driveType: 'Drive Type',
                airConditioning: 'Air Conditioning',
                yes: 'Yes',
                no: 'No',
                perDay: 'per day',
                terms: 'Terms & Conditions',
                quote: 'Quote',
                book: 'Book',
                cancel: 'Dismiss'
            },
            quote: {
                modalTitle: 'Quote Car Rental',
                pickup: 'Pickup',
                return: 'Return',
                location: '{{location}} on {{date}}',
                supplier: 'Supplier',
                vehicle: 'Vehicle',
                customer: 'Customer Name',
                notes: 'Notes',
                submit: 'Save Quote'
            }
        },
        checkout: {
            driver: {
                panelTitle: 'Primary Driver Details',
                licenseNotice: 'Primary driver must present a valid driver\'s license and a credit card in their name at the time of pickup.',
                title: 'Title',
                titles: {
                    'Mr.': 'Mr.',
                    'Mrs.': 'Mrs.',
                    'Ms.': 'Ms.',
                    'Dr.': 'Dr.'
                },
                firstName: 'First Name',
                lastName: 'Last Name',
                age: 'Age',
                flight: 'Flight Number',
                flightTooltip: 'Providing a flight number will allow the car rental company to track your client\'s flight in order to better account for airline delays and cancellations.',
                phone: 'Phone Number',
            },
            equipment: {
                panelTitle: 'Extra Equipment',
                equipmentNotice: 'All extras are paid for pickup at the supplier desk. Availability is not always graranteed and prices may be subject to change.',
                currencyNotice: 'Prices below are estimated in {{currency}} using the current exchange rates, but will usually be charged in the local currency.<br>Prices expressed in local currency may be available in the Rental Conditions.',
                taxExcl: '(tax excl.)'
            },
            payment: {
                panelTitle: 'Payment Information',
                cc: {
                    heading: 'Credit Card Information',
                    type: 'Card Type',
                    firstName: 'First Name',
                    lastName: 'Last Name',
                    number: 'Card Number',
                    expiry: 'Expiry Date',
                    expiryMonth: 'Expiry Month',
                    expiryYear: 'Expiry Year',
                    cvv: 'CVV',
                },
                billing: {
                    heading: 'Billing Address',
                    useSame: 'Same as primary driver address'
                }
            },
            errors: {
                heading: 'There are one or more errors with your form submission.',
                subheading: 'Please correct the following errors, then resubmit.',
                generic: 'Something went wrong while processing your request.',
                modalTitle: 'Something went Wrong',
                newSearch: 'Start new search',
                back: 'Back to Results',
                close: 'Close'
            },
            acceptTerms: 'I agree to the <a href="{{url}}" target="_blank">Terms &amp; Conditions</a>',
            submit: 'Book Vehicle',
            processing: {
                modalTitle: 'Making your reservation',
                body: 'Please be patient while we reserve your car rental with the supplier. This may take a few minutes.',
                notice: 'Do not navigate away from this page or close your browser until this process has completed.'
            }
        },
    },
    addressForm: {
        address: 'Street Address',
        apt: 'Apt/Suite #',
        city: 'City',
        country: 'Country',
        province: 'Province',
        state: 'State',
        postal: 'Postal Code',
        zip: 'Zip Code'
    },
    phoneForm: {
        country: 'Country Dialing Code',
        number: 'Number',
        ext: 'Ext.'
    },
    format: {
        dateLong: 'dddd, MMMM Do YYYY',
        dateTimeLong: 'MMMM Do YYYY, [at] h:mm A'
    },
    survey: {
        commentText: 'Your comments...',
        buttonText: 'Submit Your Response'
    }
};